import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import {
  CALLS_TODAY_STATS,
  CALL_REASON_META,
  callFilterCounts,
  callRowsFor,
} from '../../calls/mockData';
import type { CallFilter, CallRow } from '../../calls/types';

export interface CallsViewProps {
  filter: CallFilter;
  onFilter: (f: CallFilter) => void;
  /** Ids of patients someone else is calling right now (mock: Aaron). */
  callingBy: Record<string, string>;
  onCall: (row: CallRow) => void;
  onOpenRow: (row: CallRow) => void;
  onPrint?: () => void;
}

const FILTERS: { key: CallFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'asked', label: 'Asked to be called' },
  { key: 'treatment', label: 'Treatment' },
  { key: 'invisalign', label: 'Invisalign' },
  { key: 'balance', label: 'Balance' },
  { key: 'recall', label: 'Recall' },
];

export function CallsView({ filter, onFilter, callingBy, onCall, onOpenRow, onPrint }: CallsViewProps) {
  const counts = useMemo(() => callFilterCounts(), []);
  const [query, setQuery] = useState('');
  const [weekOpen, setWeekOpen] = useState(false);
  const [laterOpen, setLaterOpen] = useState(false);
  const [showAllToday, setShowAllToday] = useState(false);

  const rows = useMemo(() => {
    const base = callRowsFor(filter);
    const q = query.trim().toLowerCase();
    if (q === '') return base;
    return base.filter((r) => r.name.toLowerCase().includes(q) || r.phone.includes(q));
  }, [filter, query]);

  const today = rows.filter((r) => r.group === 'today');
  const week = rows.filter((r) => r.group === 'week');
  const later = rows.filter((r) => r.group === 'later');
  const s = CALLS_TODAY_STATS;
  const todayShown = showAllToday ? today : today.slice(0, 7);

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="pc-eyebrow text-[var(--text-secondary)]">{s.due} due today · {counts.all} patients</div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[26px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">Calls</h1>
          <p className="m-0 mt-0.5 max-w-[640px] text-[12.5px] text-[var(--text-secondary)]">Every call a person makes, from every agent. One row per patient, with every reason to call.</p>
        </div>
        <Button variant="secondary" size="sm" iconLeft="doc" onClick={onPrint}>Print</Button>
      </div>

      {/* today strip */}
      <section className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-[var(--r-lg)] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-3 shadow-[var(--shadow-card)]">
        <span className="pc-eyebrow text-[var(--text-muted)]">Today</span>
        <Stat v={String(s.due)} l="due" />
        <Divider />
        <Stat v={String(s.called)} l="called" />
        <Divider />
        <Stat v={String(s.noAnswer)} l="no answer" />
        <Divider />
        <Stat v={String(s.booked)} l="booked" tone="good" />
        <Divider />
        <Stat v={s.paid} l="paid on a call" tone="good" />
        <span className="ml-auto text-[11.5px] text-[var(--text-muted)]">Every call is logged with who made it.</span>
      </section>

      {/* filters + search */}
      <div className="mt-4 flex flex-wrap items-center gap-1.5">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => { onFilter(f.key); }}
            className={cn('inline-flex items-baseline gap-1.5 rounded-full border px-3 py-1 text-[12.5px]',
              f.key === filter ? 'border-[var(--brand-indigo-line)] bg-[var(--brand-indigo-tint)] font-semibold text-[var(--brand-indigo)]' : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]')}>
            {f.label}<span className="font-[var(--font-mono)] font-bold">{counts[f.key]}</span>
          </button>
        ))}
        <label className="ml-auto flex items-center gap-1.5 rounded-full border border-[var(--border-default)] px-3 py-1 text-[12.5px]">
          <span className="text-[var(--text-muted)]">Search</span>
          <input value={query} onChange={(e) => { setQuery(e.target.value); }} placeholder="a patient" aria-label="Search a patient" className="w-[120px] bg-transparent text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]" />
        </label>
      </div>

      {/* Today group */}
      <div className="mt-4 overflow-hidden rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]">
        <div className="flex flex-wrap items-baseline gap-2 border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-4 py-2.5">
          <span className="text-[13px] font-bold text-[var(--text-primary)]">Today</span>
          <span className="font-[var(--font-mono)] text-[12px] font-bold text-[var(--text-muted)]">{today.length}</span>
          <span className="text-[11.5px] text-[var(--text-muted)]">asked to be called first, then the strongest reason</span>
        </div>
        <div className="grid grid-cols-[1.4fr_2.1fr_1fr_120px] gap-x-3 border-b border-[var(--border-default)] px-4 py-2 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">
          <span>Patient</span><span>Why call</span><span>Tries</span><span />
        </div>
        {todayShown.map((r) => <Row key={r.id} r={r} callingBy={callingBy[r.id]} onCall={onCall} onOpenRow={onOpenRow} />)}
        {today.length === 0 ? <div className="px-4 py-8 text-center text-[12.5px] text-[var(--text-muted)]">Nobody to call in this filter today.</div> : null}
        {today.length > 7 && !showAllToday ? (
          <button type="button" className="w-full border-t border-[var(--border-default)] py-2.5 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--surface-sunken)]" onClick={() => { setShowAllToday(true); }}>Show {today.length - 7} more</button>
        ) : null}
      </div>

      {/* This week */}
      <GroupCard title="This week" count={week.length} sub="" open={weekOpen} onToggle={() => { setWeekOpen(!weekOpen); }}>
        {weekOpen ? (
          <div>{week.map((r) => <Row key={r.id} r={r} callingBy={callingBy[r.id]} onCall={onCall} onOpenRow={onOpenRow} />)}</div>
        ) : (
          <div className="px-4 py-3 text-[12.5px] text-[var(--text-secondary)]">
            {week.map((r) => { const t = r.reasons[0]?.type; return t !== undefined ? `${r.name} (${CALL_REASON_META[t].label})` : r.name; }).join(' · ')}
          </div>
        )}
      </GroupCard>

      {/* Later */}
      <GroupCard title="Later" count={later.length} sub="call-backs with a date, and tries resting until tomorrow" open={laterOpen} onToggle={() => { setLaterOpen(!laterOpen); }}>
        {laterOpen ? (
          <div className="flex flex-col">
            {later.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 border-t border-[var(--border-default)] px-4 py-2.5 first:border-0 text-[12.5px]">
                <button type="button" className="font-semibold text-[var(--text-primary)] hover:underline" onClick={() => { onOpenRow(r); }}>{r.name}</button>
                <span className="text-[var(--text-muted)]">{r.comesBack ?? ''}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 py-3 text-[12.5px] text-[var(--text-secondary)]">
            {later.slice(0, 3).map((r) => r.name).join(' · ')}{later.length > 3 ? ` · ${String(later.length - 3)} more` : ''} — each shows the day it comes back
          </div>
        )}
      </GroupCard>

      {/* foot note */}
      <div className="mt-5 rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-4 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
        <span className="font-bold text-[var(--text-secondary)]">How the list keeps itself.</span> No answer or voicemail: back tomorrow; after 3 tries in 7 days the patient leaves the list and ELVA carries on by text and email. Wrong number: every reason leaves. Not interested: that reason leaves for good, the others stay. Booked or paid: that reason leaves. The row goes when every reason is done.
      </div>
    </div>
  );
}

function Row({ r, callingBy, onCall, onOpenRow }: { r: CallRow; callingBy?: string | undefined; onCall: (r: CallRow) => void; onOpenRow: (r: CallRow) => void }) {
  const isCalling = callingBy !== undefined;
  return (
    <div className={cn('grid grid-cols-[1.4fr_2.1fr_1fr_120px] items-start gap-x-3 border-t border-[var(--border-default)] px-4 py-3 text-[12.5px] first:border-0', isCalling ? 'bg-[var(--warn-amber-fill)]' : 'hover:bg-[var(--surface-sunken)]')}>
      <button type="button" className="text-left" onClick={() => { onOpenRow(r); }}>
        <span className="font-semibold text-[var(--text-primary)] hover:underline">{r.name}</span>
        <span className="block text-[11px] text-[var(--text-muted)]">{r.phone}{r.askedAt !== undefined ? ` · ${r.askedAt}` : ''}{r.bestTime !== undefined ? ` · ${r.bestTime}` : ''}{r.language !== undefined ? ` · ${r.language}` : ''}</span>
      </button>
      <div className="flex flex-col gap-1.5">
        {r.reasons.map((rs, i) => {
          const meta = CALL_REASON_META[rs.type];
          const [before, after] = rs.whyLine.split('{q}');
          return (
            <div key={`${rs.type}-${String(i)}`} className="flex flex-wrap items-baseline gap-1.5">
              <span className="rounded-full px-2 py-0.5 text-[10.5px] font-bold" style={{ color: meta.fg, background: meta.bg }}>{rs.asked ? 'Asked · ' : ''}{meta.label}</span>
              <span className="text-[var(--text-secondary)]">{before}{rs.quote !== undefined ? <span className="italic text-[var(--text-primary)]">“{rs.quote}”</span> : null}{after ?? ''}</span>
            </div>
          );
        })}
      </div>
      <div className="text-[11.5px] text-[var(--text-secondary)]">
        {(() => {
          const last = r.tries.at(-1);
          if (last === undefined) return isCalling ? '—' : <span className="text-[var(--text-muted)]">Not tried yet</span>;
          return (
            <>
              <span className="font-semibold">Tried {r.tries.length}×</span>
              <span className="block text-[10.5px] text-[var(--text-muted)]">{last.at} · {last.who} · {last.outcome}</span>
            </>
          );
        })()}
      </div>
      <div className="flex justify-end">
        {isCalling ? (
          <span className="rounded-full bg-[var(--warn-amber-fill)] px-2.5 py-1 text-[11.5px] font-bold text-[var(--warn-amber)]">{callingBy} is calling</span>
        ) : (
          <Button variant="primary" size="sm" iconLeft="phone" onClick={() => { onCall(r); }}>Call</Button>
        )}
      </div>
    </div>
  );
}

function GroupCard({ title, count, sub, open, onToggle, children }: { title: string; count: number; sub: string; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div className="mt-4 overflow-hidden rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]">
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-baseline gap-2 border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-4 py-2.5 text-left">
        <span className="text-[13px] font-bold text-[var(--text-primary)]">{title}</span>
        <span className="font-[var(--font-mono)] text-[12px] font-bold text-[var(--text-muted)]">{count}</span>
        {sub !== '' ? <span className="text-[11.5px] text-[var(--text-muted)]">{sub}</span> : null}
        <span className="ml-auto text-[12px] text-[var(--brand-indigo)]">{open ? 'Hide' : 'Expand'}</span>
      </button>
      {children}
    </div>
  );
}

function Stat({ v, l, tone }: { v: string; l: string; tone?: 'good' }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={cn('font-[var(--font-mono)] text-[16px] font-bold', tone === 'good' ? 'text-[var(--money-green)]' : 'text-[var(--text-primary)]')}>{v}</span>
      <span className="text-[11.5px] text-[var(--text-muted)]">{l}</span>
    </span>
  );
}
function Divider() {
  return <span aria-hidden className="h-4 w-px bg-[var(--border-default)]" />;
}
